import { Directive, ElementRef, HostListener } from '@angular/core';

/**
 * Pone las barras de la fecha mientras se escribe: al tipear 02022025 el campo
 * muestra 02/02/2025. No hace falta agregarla en las plantillas: se aplica sola
 * a los campos de fecha de Material (datepicker y rangos desde/hasta).
 */
@Directive({
    selector: 'input[matDatepicker], input[matStartDate], input[matEndDate]',
    standalone: true
})
export class DateMaskDirective {

    /** Evita volver a formatear el evento que dispara la propia directiva */
    private reenviando = false;

    constructor(private el: ElementRef<HTMLInputElement>) { }

    @HostListener('input')
    onInput(): void {
        if (this.reenviando) {
            return;
        }

        const input = this.el.nativeElement;
        const original = input.value;
        const formateado = this.formatear(original);

        if (formateado === original) {
            return;
        }

        const alFinal = (input.selectionStart ?? original.length) >= original.length;
        const escritos = this.soloDigitos(original.slice(0, input.selectionStart ?? original.length)).length;
        input.value = formateado;

        if (alFinal) {
            this.ubicarCursor(input, formateado.length);
        } else {
            this.moverCursor(input, formateado, escritos);
        }

        // El datepicker ya leyó el valor sin barras: se le avisa del valor final
        this.reenviando = true;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        this.reenviando = false;
    }

    /**
     * Arma dd/mm/aaaa con los dígitos escritos. La barra sale sola al escribir el
     * dígito siguiente, y si el usuario la escribe él mismo se respeta: "25/" queda
     * como está y "2/" se completa a "02/", que es lo que quiso decir.
     */
    private formatear(valor: string): string {
        const cierraGrupo = /[\/\-.]$/.test(valor ?? '');
        let digitos = this.soloDigitos(valor).slice(0, 8);

        if (cierraGrupo && digitos.length === 1) {
            digitos = `0${digitos}`;
        } else if (cierraGrupo && digitos.length === 3) {
            digitos = `${digitos.slice(0, 2)}0${digitos.slice(2)}`;
        }

        let salida: string;
        if (digitos.length <= 2) {
            salida = digitos;
        } else if (digitos.length <= 4) {
            salida = `${digitos.slice(0, 2)}/${digitos.slice(2)}`;
        } else {
            salida = `${digitos.slice(0, 2)}/${digitos.slice(2, 4)}/${digitos.slice(4)}`;
        }

        // La barra recién escrita se conserva para que el usuario la vea aparecer
        if (cierraGrupo && (digitos.length === 2 || digitos.length === 4)) {
            salida += '/';
        }
        return salida;
    }

    private soloDigitos(valor: string): string {
        return (valor ?? '').replace(/\D/g, '');
    }

    /** Deja el cursor después del último dígito que el usuario había escrito */
    private moverCursor(input: HTMLInputElement, valor: string, digitos: number): void {
        let posicion = valor.length;

        if (digitos <= 0) {
            posicion = 0;
        } else {
            let contados = 0;
            for (let i = 0; i < valor.length; i++) {
                if (/\d/.test(valor[i])) {
                    contados++;
                    if (contados === digitos) {
                        posicion = i + 1;
                        break;
                    }
                }
            }
        }

        this.ubicarCursor(input, posicion);
    }

    private ubicarCursor(input: HTMLInputElement, posicion: number): void {
        try {
            input.setSelectionRange(posicion, posicion);
        } catch {
            // Algunos tipos de input no permiten mover el cursor; no es crítico
        }
    }
}
